/**
 * PermissionCard — the durable record of a granted SONMI permission.
 *
 * Shows:
 *   · the level (with SONMI's plain-English description)
 *   · granted date + actor
 *   · confidence
 *   · every level change SONMI has recorded (escalation events)
 *   · every payment SONMI has committed (the ledger)
 *   · a revoke affordance
 *
 * This is the "SONMI added this because you granted permission on
 * <date> — [Revoke permission]" experience made concrete.
 */
import React, { useState } from "react";
import { sonmiCopy } from "@/lib/sonmiCopy";
import { ShieldCheck, ShieldOff, History } from "lucide-react";

const fmtDollar = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// e.g. "2026-08" → "August 2026"
function monthLabelFromStartsIso(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + (iso.length === 7 ? "-01T00:00:00Z" : "T00:00:00Z"));
    return d.toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
  } catch {
    return iso;
  }
}

export function PermissionCard({ permission, obligation, ledger = [], onRevoke, employee }) {
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState("");

  const revoked = !!permission.revoked_at;
  const totalCount = obligation?.schedule?.total_count || 15;
  const paidCount = ledger.length;

  return (
    <div
      className={
        "rounded-2xl border overflow-hidden " +
        (revoked ? "border-stone-200 bg-stone-50/50" : "border-stone-200 bg-white")
      }
      data-testid={`permission-card-${permission.permission_id}`}
    >
      <header className="px-6 pt-5 pb-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500 mb-1">
            {revoked ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            <span>{revoked ? "No longer managing" : "SONMI is managing this"}</span>
          </div>
          <h3 className="text-lg font-semibold text-stone-900" data-testid={`perm-employee-name`}>
            {employee?.display_name || "Employee"}
          </h3>
          <div className="text-xs text-stone-500 mt-2 leading-relaxed">
            {revoked
              ? sonmiCopy.permission.revokedAt(permission.revoked_at, permission.revoked_by)
              : sonmiCopy.permission.grantedAt(permission.granted_at, permission.granted_by)}
            {permission.reason && (
              <div className="italic mt-0.5">"{permission.reason}"</div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-stone-500">Progress</div>
          <div className="text-2xl font-semibold text-stone-900 mt-1 font-variant-numeric tabular-nums">
            {paidCount} <span className="text-stone-400 text-lg">/ {totalCount}</span>
          </div>
        </div>
      </header>

      {/* Ledger */}
      <div className="border-t border-stone-100 px-6 py-4">
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">
          {sonmiCopy.permission.ledgerHeading}
        </div>
        {ledger.length === 0 ? (
          <>
            <div className="text-sm text-stone-500 italic">{sonmiCopy.permission.ledgerEmpty}</div>
            {obligation?.schedule?.starts_on_or_after && (
              <div className="mt-2 text-sm text-stone-700"
                   data-testid={`next-expected-${permission.permission_id}`}>
                Next payment: first payroll in{" "}
                <strong>{monthLabelFromStartsIso(obligation.schedule.starts_on_or_after.slice(0, 7))}</strong>.
              </div>
            )}
          </>
        ) : (
          <div className="space-y-1">
            {ledger.map((row) => (
              <div key={`${row.obligation_id}-${row.payment_number}`}
                   className="flex items-center justify-between text-sm text-stone-800"
                   data-testid={`ledger-row-${row.payment_number}`}>
                <span>
                  Payment <strong>{row.payment_number}</strong> of {totalCount}
                  <span className="text-stone-500 ml-2">
                    · {row.resolved_status} · on {new Date(row.issued_at).toLocaleDateString()}
                  </span>
                </span>
                <span className="font-variant-numeric tabular-nums font-medium">
                  {fmtDollar(row.amount_cents)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confidence / level events — hidden in the sign-on proof
          because the responsibility-level concept is not exposed in
          this workflow. The events remain in the DB for future use. */}
      {false && (permission.confidence_events || []).length > 0 && (
        <div className="border-t border-stone-100 px-6 py-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1">
            <History className="w-3.5 h-3.5" /> {sonmiCopy.permission.confidenceEventsHeading}
          </div>
        </div>
      )}

      {/* Revoke */}
      {!revoked && (
        <footer className="border-t border-stone-100 px-6 py-4 bg-stone-50/40">
          {!confirmingRevoke ? (
            <button
              type="button"
              onClick={() => setConfirmingRevoke(true)}
              className="text-sm text-stone-600 hover:text-stone-900"
              data-testid={`revoke-btn-${permission.permission_id}`}
            >
              {sonmiCopy.common.revoke}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-stone-700">
                Tell SONMI why you're taking this back — she'll record it and stop acting on it.
              </div>
              <input
                type="text"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="Reason (optional)"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg bg-white text-sm"
                data-testid={`revoke-reason-input`}
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setConfirmingRevoke(false); setRevokeReason(""); }}
                  className="px-3 py-1.5 rounded-lg text-sm text-stone-600 hover:bg-stone-100"
                >
                  {sonmiCopy.common.cancel}
                </button>
                <button
                  type="button"
                  onClick={() => onRevoke(permission.permission_id, revokeReason)}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-medium text-white bg-stone-900 hover:bg-stone-800"
                  data-testid={`revoke-confirm-${permission.permission_id}`}
                >
                  {sonmiCopy.common.revoke}
                </button>
              </div>
            </div>
          )}
        </footer>
      )}
    </div>
  );
}
